import React, { FC, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import stringifyObject from 'stringify-object';
import { overrides } from '@cld/button';
import CopyToClipboard from '@cld/copy-to-clipboard';
import { Icon } from '@cld/icon';
import { CodeTabSectionProps, ButtonsWrapper, ExpandButton } from '@cld/widget-demos-common';
import { Section } from '../../common/Section';
import { messages } from '../../../../i18n';
import { useAuth, useWidgetConfiguration } from '../../../../selectors';
import { mapStateToWidgetParameter } from '../../../../services/widget-service';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { HalfHeightCodeWrapper } from './common/HalfHeightCodeWrapper';

const { Button } = overrides.mediaLibraryWidgetDemoOverrides;

const getEmbedCode = (data: Record<string, unknown>) => {
  const config = stringifyObject({
    ...mapStateToWidgetParameter(data),
    ...{ button_class: 'myBtn', button_caption: 'Select Image or Video' }
  }, {
    indent: '  ',
    singleQuotes: true,
  });
  return `window.ml = cloudinary.createMediaLibrary(${config}, {
  insertHandler: function (data) {
    data.assets.forEach(asset => { console.log("Inserted asset:",
      JSON.stringify(asset, null, 2)) })
    }
  },
  document.getElementById("open-btn")
);`;
};

export const EmbedCode: FC<CodeTabSectionProps> = ({ isExpanded, onExpandToggle }) => {
  const analytics = useAnalytics();
  const { formatMessage } = useIntl();
  const auth = useAuth();
  const configuration = useWidgetConfiguration();
  const embedCode = getEmbedCode({ ...auth, ...configuration });

  const handleExpandButtonClick = useCallback(() => {
    const action = !isExpanded ? 'expand' : 'collapse';
    analytics(action, `${action} embed code`);
    onExpandToggle();
  }, [isExpanded, analytics, onExpandToggle]);

  const handleCopyEmbedCode = useCallback(() => {
    analytics('copy to clipboard', 'copy media library configuration to clipboard');
  }, [analytics]);

  return (
    <Section title={formatMessage(messages['demo.codeSection.embedCode.title'])}>
      <HalfHeightCodeWrapper isExpanded={isExpanded}>
        {embedCode}
      </HalfHeightCodeWrapper>
      <ButtonsWrapper>
        <CopyToClipboard tooltipAfterText={formatMessage(messages['demo.codeSection.embedCode.afterCopyTooltip'])} copiedValue={embedCode}>
          <Button size="sm" onClick={handleCopyEmbedCode} data-test="copy-code-to-clipboard">
            <Icon name="copy" />
            <FormattedMessage {...messages['demo.codeSection.embedCode.copyCodeToClipboard']} />
          </Button>
        </CopyToClipboard>
        <ExpandButton isSectionExpanded={isExpanded} onClick={handleExpandButtonClick} data-test="expand-embed" />
      </ButtonsWrapper>
    </Section>
  );
};
